import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, MessageCircle, RefreshCw, AlertTriangle, ChevronRight } from 'react-feather';
import { XIcon } from './XIcon';
import { integrationsAPI, XDMMessage, CanSendDMResponse } from '../services/integrationsAPI';
import { useAuth } from '../contexts/AuthContext';
import { userAPI, familyAPI } from '../services/api';
import { socketService } from '../services/socketService';
import { useToast } from '../contexts/ToastContext';
import Modal, { ModalHandle } from './Modal';
import { useResponsive } from '../hooks/useResponsive';
import { TextArea } from './ui';

interface DMCoach {
  name: string;
  twitter_handle: string;
  coach_id?: string;
  school_id?: string;
}

interface DMCoachModalProps {
  isOpen: boolean;
  onClose: () => void;
  coach: DMCoach;
  /** Render inside the unified CoachContactModal shell (no own overlay). */
  embedded?: boolean;
  /** Close with a toast after a DM sends. Off, the window stays open as a chat. */
  closeOnSend?: boolean;
  /** Fires after onClose when closeOnSend closed the window. */
  onSent?: () => void;
}

const DMCoachModal: React.FC<DMCoachModalProps> = ({
  isOpen,
  onClose,
  coach,
  embedded = false,
  closeOnSend = false,
  onSent,
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [athleteId, setAthleteId] = useState<string | null>(null);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);
  const [isXConnected, setIsXConnected] = useState(false);
  const [messages, setMessages] = useState<XDMMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [canSendDM, setCanSendDM] = useState<boolean | null>(null);
  const [isCheckingCanSend, setIsCheckingCanSend] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // False until a conversation's first paint has been positioned at the bottom,
  // so the initial jump is instant (no visible scroll animation on open) while
  // live/sent messages still animate smoothly. Reset when the modal reopens.
  const didInitialScrollRef = useRef(false);

  // Load athlete data & check X connection when modal opens
  useEffect(() => {
    if (isOpen && sessionToken) {
      setIsCheckingConnection(true);
      didInitialScrollRef.current = false;
      setMessages([]);
      setConversationId(null);
      setMessageText('');
      setCanSendDM(null);
      loadAthleteAndConnect();
    }
  }, [isOpen, sessionToken]);

  // Load conversation history when athleteId is ready
  useEffect(() => {
    if (athleteId && isXConnected && coach.coach_id) {
      loadConversationHistory();
    }
  }, [athleteId, isXConnected, coach.coach_id]);

  // Auto-scroll to the latest message. Instant on the conversation's first
  // paint (the thread simply appears at the bottom — no scroll animation on
  // open), smooth only for messages that arrive or are sent while it's open.
  useEffect(() => {
    if (!messages.length) return;
    messagesEndRef.current?.scrollIntoView({
      behavior: didInitialScrollRef.current ? 'smooth' : 'auto',
      block: 'end',
    });
    didInitialScrollRef.current = true;
  }, [messages]);

  const loadAthleteAndConnect = async () => {
    if (!sessionToken) return;

    try {
      const userProfile = await userAPI.getProfile(sessionToken);
      const familyResult = await familyAPI.getMyFamily(sessionToken);

      let currentAthleteId: string | null = null;

      if (familyResult.family?.members) {
        const familyMember = familyResult.family.members.find(
          (member: any) => member.id === userProfile.id
        );
        if (familyMember?.athlete_profile_id) {
          currentAthleteId = familyMember.athlete_profile_id;
        }
      }

      if (!currentAthleteId) {
        // Fallback
        const { getMyProfile } = await import('../services/api').then(m => m.athleteAPI);
        const result = await getMyProfile(sessionToken);
        if (result.profile) {
          currentAthleteId = result.profile.id;
        }
      }

      if (currentAthleteId) {
        setAthleteId(currentAthleteId);
        const connectionStatus = await integrationsAPI.getConnectionStatus(currentAthleteId);
        setIsXConnected(connectionStatus.twitter);

        // Check if we can DM this coach
        if (connectionStatus.twitter && coach.twitter_handle) {
          setIsCheckingCanSend(true);
          try {
            const canSendResult = await integrationsAPI.canSendDM(currentAthleteId, coach.twitter_handle);
            setCanSendDM(canSendResult.data.can_send_dm);
            // A needs_reauth reason means the X token lacks the DM scope — surface
            // the reconnect banner (it's a user-token issue, not a coach setting).
            if (canSendResult.data.reason === 'needs_reauth') setNeedsReconnect(true);
          } catch (error) {
            console.error('Failed to check DM eligibility:', error);
            setCanSendDM(false);
          } finally {
            setIsCheckingCanSend(false);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load athlete data for DM:', error);
      setIsXConnected(false);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  const loadConversationHistory = async () => {
    if (!athleteId || !coach.coach_id) return;

    setIsLoadingHistory(true);
    try {
      const result = await integrationsAPI.getXDMConversation(athleteId, coach.coach_id);
      if (result.success) {
        setMessages(result.data.messages || []);
        setConversationId(result.data.conversation_id);
      }
    } catch (error) {
      console.error('Failed to load DM history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Silent refresh of the conversation (no loading indicator). Used by both the
  // live socket handler and the backup poll.
  const pollForNewMessages = useCallback(async () => {
    if (!athleteId || !coach.coach_id) return;
    // audit #151: don't burn network while the tab is backgrounded — the modal
    // being open isn't enough on its own.
    if (typeof document !== 'undefined' && document.hidden) return;
    try {
      const result = await integrationsAPI.getXDMConversation(athleteId, coach.coach_id);
      if (result.success) {
        const newMessages = result.data.messages || [];
        if (newMessages.length !== messages.length) {
          setMessages(newMessages);
          setConversationId(result.data.conversation_id);
        }
      }
    } catch (error) {
      // Silent fail on poll
    }
  }, [athleteId, coach.coach_id, messages.length]);

  // audit #151: prefer the live socket for DM updates. When a `dm:message`
  // event arrives while this thread is open, refresh immediately instead of
  // waiting for the next poll tick.
  // TODO(audit #151): the backend does not yet emit `dm:message` to the user
  // room; once it does, this becomes the primary channel and the backup poll
  // below can be dropped entirely.
  useEffect(() => {
    if (!(isOpen && athleteId && isXConnected && coach.coach_id)) return;
    const handleDmMessage = () => { pollForNewMessages(); };
    socketService.subscribe('dm:message', handleDmMessage);
    return () => {
      socketService.unsubscribe('dm:message', handleDmMessage);
    };
  }, [isOpen, athleteId, isXConnected, coach.coach_id, pollForNewMessages]);

  // Backup poll while the modal is open + connected. Raised from 10s to 30s and
  // visibility-gated (guard in pollForNewMessages) now that the socket carries
  // the fast path (audit #151).
  useEffect(() => {
    if (isOpen && athleteId && isXConnected && coach.coach_id && !isLoadingHistory) {
      pollIntervalRef.current = setInterval(pollForNewMessages, 30000);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isOpen, athleteId, isXConnected, coach.coach_id, isLoadingHistory, pollForNewMessages]);

  // Manual refresh handler
  const handleRefresh = async () => {
    if (!athleteId || !coach.coach_id || isRefreshing) return;
    setIsRefreshing(true);
    try {
      const result = await integrationsAPI.getXDMConversation(athleteId, coach.coach_id);
      if (result.success) {
        setMessages(result.data.messages || []);
        setConversationId(result.data.conversation_id);
      }
    } catch (error) {
      console.error('Failed to refresh DM history:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSendDM = async () => {
    if (!athleteId || !messageText.trim()) return;

    setIsSending(true);
    try {
      const cleanedHandle = coach.twitter_handle?.replace(/^@/, '') || '';
      const result = await integrationsAPI.sendXDM(athleteId, {
        text: messageText.trim(),
        coach_id: coach.coach_id,
        // For first message: include x_handle so backend can resolve the X user ID
        // For follow-ups: include dm_conversation_id
        ...(conversationId
          ? { dm_conversation_id: conversationId }
          : cleanedHandle ? { x_handle: cleanedHandle } : {}),
      });

      if (result.success) {
        if (closeOnSend) {
          toast.success(`DM sent to ${coach.name}.`);
          handleClose();
          onSent?.();
          return;
        }
        // Add the sent message to the UI immediately
        const newMessage: XDMMessage = {
          id: result.data.dm_event_id,
          dm_event_id: result.data.dm_event_id,
          conversation_id: result.data.dm_conversation_id,
          event_type: 'MessageCreate',
          sender_id: '',
          text: messageText.trim(),
          is_sent_by_user: true,
          event_created_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
          attachments: null,
          participant_ids: [],
          coach_ids: coach.coach_id ? [coach.coach_id] : [],
          school_ids: coach.school_id ? [coach.school_id] : [],
        };

        setMessages(prev => [...prev, newMessage]);
        setConversationId(result.data.dm_conversation_id);
        setMessageText('');
        inputRef.current?.focus();
      } else {
        // #196: a success:false response previously gave zero feedback.
        toast.error((result as any)?.message || 'Failed to send DM. Please try again.');
      }
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to send DM';
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (messageText.trim() && !isSending) {
        handleSendDM();
      }
    }
  };

  // Route this composer's own X/Cancel through the Modal's guarded close so the
  // "Discard changes?" prompt fires from them too (not just backdrop/Escape).
  const composerModalRef = useRef<ModalHandle>(null);
  const handleClose = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setMessages([]);
    setConversationId(null);
    setMessageText('');
    setAthleteId(null);
    setIsCheckingConnection(true);
    setNeedsReconnect(false);
    setCanSendDM(null);
    onClose();
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) {
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
      d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  if (!isOpen) return null;

  const cleanHandle = coach.twitter_handle?.replace(/^@/, '') || '';

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        ref={composerModalRef}
        inline={embedded}
        isDirty={messageText.trim().length > 0}
        disableClose={isSending}
        ariaLabel={`Direct message ${coach.name}`}
        zIndex={1001}
        contentStyle={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: isMobile ? 'calc(100vw - 32px)' : '100%',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '550px',
          height: isMobile ? 'calc(100vh - 100px)' : '70vh',
          maxHeight: '700px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
      >
          {/* Header */}
          <div style={{
            backgroundColor: '#151133',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <button
              onClick={() => (composerModalRef.current ? composerModalRef.current.requestClose() : handleClose())}
              disabled={isSending}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={22} />
            </button>
            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <XIcon size={16} color="#000000" />
                <h2 style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white'
                }}>
                  {coach.name}
                </h2>
              </div>
              {cleanHandle && (
                <div style={{
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.6)',
                  marginTop: '2px'
                }}>
                  @{cleanHandle}
                </div>
              )}
              <div style={{
                fontSize: '10px',
                color: '#000000',
                fontWeight: '600',
                marginTop: '3px',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>
                X Direct Message
              </div>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || !isXConnected}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: (isRefreshing || !isXConnected) ? 'default' : 'pointer',
                padding: '8px',
                minWidth: '44px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: (!isXConnected) ? 0 : 1,
                transition: 'transform 0.3s',
                animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
              }}
            >
              <RefreshCw size={18} />
            </button>
          </div>

          {/* Content Area */}
          {isCheckingConnection ? (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#F9FAFB'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#F3F4F6',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }}>
                  <MessageCircle size={32} color="#000000" />
                </div>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                  Connecting...
                </p>
              </div>
            </div>
          ) : !isXConnected ? (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#F9FAFB',
              padding: '32px'
            }}>
              <div style={{ textAlign: 'center', maxWidth: '300px' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#F3F4F6',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <MessageCircle size={32} color="#000000" />
                </div>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#1F2937',
                  margin: '0 0 8px'
                }}>
                  X Account Not Connected
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  lineHeight: '1.5',
                  margin: '0 0 12px'
                }}>
                  To send direct messages, connect your X account first.
                </p>
                <p style={{
                  fontSize: '13px',
                  color: '#F9550D',
                  margin: 0
                }}>
                  Go to Messages <ChevronRight size={13} style={{ display: 'inline', verticalAlign: 'middle' }} aria-hidden="true" /> Settings to connect your X account.
                </p>
              </div>
            </div>
          ) : isCheckingCanSend ? (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#F9FAFB'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#F3F4F6',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }}>
                  <MessageCircle size={32} color="#000000" />
                </div>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                  Checking DM availability...
                </p>
              </div>
            </div>
          ) : canSendDM === false ? (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#F9FAFB',
              padding: '32px'
            }}>
              <div style={{ textAlign: 'center', maxWidth: '320px' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#FEF2F2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <AlertTriangle size={28} color="#EF4444" />
                </div>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#1F2937',
                  margin: '0 0 8px'
                }}>
                  Can't DM This Coach
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  lineHeight: '1.5',
                  margin: '0 0 16px'
                }}>
                  This coach hasn't enabled direct messages on X, or their account settings prevent receiving DMs.
                </p>
                <a
                  href={cleanHandle ? `https://x.com/${cleanHandle}` : 'https://x.com'}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 20px',
                    backgroundColor: '#000000',
                    borderRadius: '20px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Manrope, sans-serif'
                  }}
                >
                  <XIcon size={16} />
                  View Profile on X
                </a>
              </div>
            </div>
          ) : (
            <>
              {/* Reconnect Banner */}
              {needsReconnect && (
                <div style={{
                  backgroundColor: '#FEF3C7',
                  padding: '10px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  borderBottom: '1px solid #FDE68A'
                }}>
                  <AlertTriangle size={16} color="#D97706" />
                  <p style={{ fontSize: '13px', color: '#92400E', margin: 0, lineHeight: '1.4' }}>
                    Your X account needs DM permissions. Go to <strong>Messages <ChevronRight size={13} style={{ display: 'inline', verticalAlign: 'middle' }} aria-hidden="true" /> Settings</strong> and reconnect your X account.
                  </p>
                </div>
              )}

              {/* Messages Area */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                backgroundColor: '#F9FAFB',
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {isLoadingHistory ? (
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <p style={{ fontSize: '14px', color: '#9CA3AF' }}>Loading messages...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '32px 16px'
                  }}>
                    <div style={{
                      width: '56px',
                      height: '56px',
                      backgroundColor: '#F3F4F6',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '12px'
                    }}>
                      <MessageCircle size={28} color="#000000" />
                    </div>
                    <p style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#374151',
                      margin: '0 0 4px'
                    }}>
                      No messages yet
                    </p>
                    <p style={{
                      fontSize: '13px',
                      color: '#9CA3AF',
                      margin: 0
                    }}>
                      Send an X Direct Message to {coach.name}
                    </p>
                  </div>
                ) : (
                  <>
                    {messages.map((msg, index) => (
                      <div
                        key={msg.dm_event_id || index}
                        style={{
                          maxWidth: '80%',
                          marginLeft: msg.is_sent_by_user ? 'auto' : '0',
                          marginRight: msg.is_sent_by_user ? '0' : 'auto',
                          marginBottom: '10px',
                          animation: 'fadeIn 0.2s ease-out'
                        }}
                      >
                        <div style={{
                          padding: '10px 14px',
                          borderRadius: msg.is_sent_by_user
                            ? '14px 14px 4px 14px'
                            : '14px 14px 14px 4px',
                          backgroundColor: msg.is_sent_by_user
                            ? '#000000'
                            : 'white',
                          color: msg.is_sent_by_user ? 'white' : '#1F2937',
                          boxShadow: msg.is_sent_by_user
                            ? 'none'
                            : '0 1px 3px rgba(0,0,0,0.08)',
                          fontSize: '14px',
                          lineHeight: '1.5',
                          wordBreak: 'break-word' as const
                        }}>
                          {msg.text}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          color: '#9CA3AF',
                          marginTop: '4px',
                          textAlign: msg.is_sent_by_user ? 'right' : 'left',
                          paddingLeft: msg.is_sent_by_user ? '0' : '4px',
                          paddingRight: msg.is_sent_by_user ? '4px' : '0'
                        }}>
                          {formatTime(msg.event_created_at || msg.created_at)}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Message Input / Continue on X */}
              <div style={{
                borderTop: '1px solid #E5E7EB',
                backgroundColor: 'white',
                display: 'flex',
                flexDirection: 'column',
              }}>
                {/* Input always shown: in-app DM sends for BOTH a new conversation
                    and follow-ups (via dm_conversation_id). A secondary link below
                    opens the full thread on X. */}
                {(
                <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  padding: '6px 0',
                  backgroundColor: '#F0F9FF',
                  borderBottom: '1px solid #E0F2FE'
                }}>
                  <XIcon size={12} color="#000000" />
                  <span style={{ fontSize: '11px', color: '#000000', fontWeight: '600' }}>
                    Sending via X Direct Messages
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', padding: '12px 16px' }}>
                {/* A chat composer, so it keeps the pill radius, the single
                    row and resize:none that make it grow by script rather than
                    by handle. What it loses is the inline `outline: none` and
                    the onFocus/onBlur border swap that stood in for a focus
                    indicator and did not survive keyboard focus. */}
                <TextArea
                  ref={inputRef}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  aria-label="Message"
                  disabled={isSending}
                  rows={1}
                  style={{
                    width: 'auto',
                    flex: 1,
                    padding: '10px 14px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    resize: 'none',
                    maxHeight: '100px',
                    lineHeight: '1.4'
                  }}
                />
                <button
                  onClick={handleSendDM}
                  aria-label="Send"
                  disabled={!messageText.trim() || isSending}
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: (!messageText.trim() || isSending) ? '#E5E7EB' : '#000000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: (!messageText.trim() || isSending) ? 'not-allowed' : 'pointer',
                    flexShrink: 0,
                    transition: 'background-color 0.2s'
                  }}
                >
                  <Send
                    size={18}
                    color={(!messageText.trim() || isSending) ? '#9CA3AF' : 'white'}
                    style={{ marginLeft: '2px' }}
                  />
                </button>
                </div>
                </>
                )}
                {messages.length > 0 && (
                  <a
                    href="https://x.com/messages"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px 16px 12px', color: '#000000', fontSize: '13px', fontWeight: 600, textDecoration: 'none', fontFamily: 'Manrope, sans-serif' }}
                  >
                    <XIcon size={14} />
                    Or continue this thread on X
                  </a>
                )}
              </div>
            </>
          )}
        </Modal>
    </>
  );
};

export default DMCoachModal;
